import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faFolder,
  faFileLines,
  faLightbulb,
  faCirclePlay,
  faDownload,
  faBook,
} from '@fortawesome/pro-light-svg-icons';
import {
  BBIntroRoot,
  BBIntroScroll,
  BBIntroHeader,
  BBIntroTabs,
  BBIntroTab,
  BBIntroTabSep,
  BBIntroTitle,
  BBIntroSubtitle,
  BBIntroBody,
  BBIntroBlocks,
  BBIntroBlock,
  BBIntroBlockTitle,
  BBIntroTitleIcon,
  BBIntroOptional,
  BBIntroBlockText,
  BBIntroInfo,
  BBIntroBefore,
  BBIntroBeforeTitle,
  BBIntroCards,
  BBIntroCard,
  BBIntroCardIcon,
  BBIntroCardTitle,
  BBIntroCardText,
  BBIntroFooter,
  BBIntroFooterInner,
  BBIntroFooterText,
  BBIntroFooterAction,
  BBIntroTerms,
  BBButton,
  BBLink,
} from './styles';
import { PARTNER_REPORT_QUESTIONS_URL } from '../../../shared/components/DownloadReportQuestions';

interface BbIntroProps {
  buttonLabel: string;
  onEnter: () => void;
}

const HELP_CENTER_URL =
  'https://support.trueimpact.com/nonprofit-impact-reporting-resources';
const OVERVIEW_URL =
  'https://support.trueimpact.com/getting-started-with-the-impact-reporting-network';

interface BbIntroActionCardProps {
  icon: IconProp;
  title: string;
  href: string;
  children: React.ReactNode;
}

const BbIntroActionCard: React.FC<BbIntroActionCardProps> = ({
  icon,
  title,
  href,
  children,
}) => {
  const open = () => window.open(href, '_blank', 'noopener,noreferrer');
  return (
    <BBIntroCard
      onClick={open}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
    >
      <BBIntroCardIcon>
        <FontAwesomeIcon icon={icon} />
      </BBIntroCardIcon>
      <BBIntroCardTitle>{title}</BBIntroCardTitle>
      <BBIntroCardText>{children}</BBIntroCardText>
    </BBIntroCard>
  );
};

const BbIntro: React.FC<BbIntroProps> = ({ buttonLabel, onEnter }) => (
  <BBIntroRoot>
    <BBIntroScroll>
      <BBIntroHeader>
        <BBIntroTabs>
          <BBIntroTab data-active="true">Blackbaud</BBIntroTab>
          <BBIntroTabSep>|</BBIntroTabSep>
          <BBIntroTab>trueimpact</BBIntroTab>
        </BBIntroTabs>
        <BBIntroTitle>Share Your Programs</BBIntroTitle>
        <BBIntroSubtitle>
          Set up program profiles to make your work searchable on Blackbaud.
          <br />
          Impact reports are optional additions you can add anytime.
        </BBIntroSubtitle>
      </BBIntroHeader>

      <BBIntroBody>
        <BBIntroBlocks>
          <BBIntroBlock>
            <BBIntroBlockTitle>
              <BBIntroTitleIcon>
                <FontAwesomeIcon icon={faFolder} />
              </BBIntroTitleIcon>
              Program profile
            </BBIntroBlockTitle>
            <BBIntroBlockText>
              Your program&apos;s core overview — cause area, activities, and
              impact model. Set up once, reuse across reports. Makes your
              program searchable on Blackbaud immediately.
            </BBIntroBlockText>
          </BBIntroBlock>
          <BBIntroBlock>
            <BBIntroBlockTitle>
              <BBIntroTitleIcon>
                <FontAwesomeIcon icon={faFileLines} />
              </BBIntroTitleIcon>
              Impact reports <BBIntroOptional>— optional</BBIntroOptional>
            </BBIntroBlockTitle>
            <BBIntroBlockText>
              Annual snapshots or full reports within a program. Create one
              universal report and share it across multiple donors — no
              duplication of effort.
            </BBIntroBlockText>
          </BBIntroBlock>
        </BBIntroBlocks>

        <BBIntroInfo>
          <BBIntroTitleIcon>
            <FontAwesomeIcon icon={faLightbulb} />
          </BBIntroTitleIcon>
          <strong>No requirements, no due dates.</strong> Add what you have,
          when you have it. A profile alone is a great start — you can add
          reports anytime.
        </BBIntroInfo>

        <BBIntroBefore>
          <BBIntroBeforeTitle>Before you begin</BBIntroBeforeTitle>
          <BBIntroCards>
            <BbIntroActionCard
              icon={faCirclePlay}
              title="Watch the overview"
              href={OVERVIEW_URL}
            >
              Onboarding video
            </BbIntroActionCard>
            <BbIntroActionCard
              icon={faDownload}
              title="Download the question set"
              href={PARTNER_REPORT_QUESTIONS_URL}
            >
              Covers both Impact Snapshots and full reports
            </BbIntroActionCard>
            <BbIntroActionCard
              icon={faBook}
              title="Help Center"
              href={HELP_CENTER_URL}
            >
              Reporting resources &amp; FAQs
            </BbIntroActionCard>
          </BBIntroCards>
        </BBIntroBefore>
      </BBIntroBody>
    </BBIntroScroll>

    <BBIntroFooter>
      <BBIntroFooterInner>
        <BBIntroFooterText>
          <strong>Ready to get started?</strong>
          <span>Set up your first program profile to appear on Blackbaud.</span>
        </BBIntroFooterText>
        <BBIntroFooterAction>
          <BBButton onClick={onEnter} variant="contained" size="medium">
            {buttonLabel} →
          </BBButton>
          <BBIntroTerms>
            By clicking &quot;{buttonLabel}&quot;, you agree to True Impact&apos;s{' '}
            <BBLink
              href="https://www.trueimpact.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </BBLink>
            .
          </BBIntroTerms>
        </BBIntroFooterAction>
      </BBIntroFooterInner>
    </BBIntroFooter>
  </BBIntroRoot>
);

export default BbIntro;
